import { useEffect, useMemo, useState, type FormEvent } from "react";
import { usePerspective } from "../lib/perspective";
import {
  useFormsList, useFormTemplates, useFormDetail, useFormResponses, useFormAggregation,
  useFormAssignments, useCreateForm, useUpdateForm, useSetFormStatus, useDeleteForm,
  useFormFromTemplate, useSubmitResponse, useDeleteResponse, useExportCsv,
  useCreateAssignment, useCancelAssignment,
  FIELD_TYPE_LABEL, CONTEXT_TYPE_LABEL, FORM_STATUS_LABEL,
  type FieldType, type FormField, type FormInput, type FormSummary, type FormAggregateField,
} from "../lib/formulare";

type View = "list" | "builder" | "fill" | "responses";
type FieldDraft = { id?: string; label: string; type: FieldType; required: boolean; optionsText: string; help: string };

const FIELD_TYPES: FieldType[] = ["text", "textarea", "number", "select", "date", "checkbox"];
const CONTEXT_TYPES = ["frei", "prozessschritt", "ressort", "usecase"];

function toDraft(field: FormField): FieldDraft {
  return {
    id: field.id, label: field.label, type: field.type, required: field.required,
    optionsText: (field.options || []).join(", "), help: field.help || "",
  };
}

function fromDraft(draft: FieldDraft): Partial<FormField> {
  return {
    id: draft.id, label: draft.label, type: draft.type, required: draft.required, help: draft.help,
    options: draft.type === "select" ? draft.optionsText.split(/[,\n]/).map((value) => value.trim()).filter(Boolean) : [],
  };
}

function AggregateField({ field }: { field: FormAggregateField }) {
  if (field.counts) {
    const entries = Object.entries(field.counts);
    const maximum = Math.max(1, ...entries.map(([, count]) => count));
    return (
      <div className="fm-agg-field">
        <div className="fm-agg-title"><strong>{field.label}</strong><span>{field.answered} beantwortet</span></div>
        <div className="fm-bars">
          {entries.map(([label, count]) => (
            <div className="fm-bar-row" key={label}>
              <span>{label}</span>
              <div className="fm-bar-track"><i style={{ width: `${Math.round(count / maximum * 100)}%` }} /></div>
              <strong>{count}</strong>
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (field.type === "number") {
    return (
      <div className="fm-agg-field">
        <div className="fm-agg-title"><strong>{field.label}</strong><span>{field.count ?? 0} Werte</span></div>
        <div className="fm-number-stats">
          <span><small>Mittelwert</small><strong>{field.mean ?? "—"}</strong></span>
          <span><small>Minimum</small><strong>{field.min ?? "—"}</strong></span>
          <span><small>Maximum</small><strong>{field.max ?? "—"}</strong></span>
        </div>
      </div>
    );
  }
  return (
    <div className="fm-agg-field fm-agg-compact">
      <strong>{field.label}</strong><span>{field.count ?? field.answered} Antworten</span>
    </div>
  );
}

export default function FormularePage() {
  const { perspective } = usePerspective();
  const role = perspective === "kunde" ? "kunde" : "berater";
  const isBerater = role === "berater";

  const [view, setView] = useState<View>("list");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [message, setMessage] = useState("");
  const [assigning, setAssigning] = useState<FormSummary | null>(null);

  const list = useFormsList("all", "");
  const templates = useFormTemplates();
  const detail = useFormDetail(view === "fill" || view === "builder" ? selectedId : null);
  const responses = useFormResponses(view === "responses" ? selectedId : null);
  const aggregation = useFormAggregation(view === "responses" ? selectedId : null);
  const assignments = useFormAssignments(assigning?.id ?? null, "all");
  const fillAssignments = useFormAssignments(view === "fill" ? selectedId : null, "offen");

  const createForm = useCreateForm();
  const updateForm = useUpdateForm();
  const setStatus = useSetFormStatus();
  const deleteForm = useDeleteForm();
  const fromTemplate = useFormFromTemplate();
  const submit = useSubmitResponse();
  const deleteResponse = useDeleteResponse();
  const createAssignment = useCreateAssignment();
  const cancelAssignment = useCancelAssignment();
  const exportCsv = useExportCsv();

  const [builderTitle, setBuilderTitle] = useState("");
  const [builderDescription, setBuilderDescription] = useState("");
  const [builderContext, setBuilderContext] = useState("frei");
  const [builderContextRef, setBuilderContextRef] = useState("");
  const [builderAssignedRole, setBuilderAssignedRole] = useState("");
  const [builderFields, setBuilderFields] = useState<FieldDraft[]>([]);
  const editingId = useMemo(() => view === "builder" ? selectedId : null, [view, selectedId]);

  const [answers, setAnswers] = useState<Record<string, unknown>>({});
  const [assignRole, setAssignRole] = useState<"kunde" | "berater" | "alle">("kunde");
  const [assignLabel, setAssignLabel] = useState("");
  const [assignDue, setAssignDue] = useState("");

  useEffect(() => {
    if (view === "builder" && selectedId && detail.data?.form) {
      const form = detail.data.form;
      setBuilderTitle(form.title);
      setBuilderDescription(form.description);
      setBuilderContext(form.context_type);
      setBuilderContextRef(form.context_ref);
      setBuilderAssignedRole(form.assigned_role);
      setBuilderFields((form.fields || []).map(toDraft));
    }
  }, [view, selectedId, detail.data]);

  useEffect(() => {
    if (view === "fill") setAnswers({});
  }, [view, selectedId]);

  function newBuilder() {
    setSelectedId(null);
    setBuilderTitle("");
    setBuilderDescription("");
    setBuilderContext("frei");
    setBuilderContextRef("");
    setBuilderAssignedRole("");
    setBuilderFields([{ label: "", type: "text", required: false, optionsText: "", help: "" }]);
    setMessage("");
    setView("builder");
  }

  function editBuilder(id: string) {
    setSelectedId(id);
    setMessage("");
    setView("builder");
  }

  function saveBuilder(event: FormEvent) {
    event.preventDefault();
    if (!builderTitle.trim()) {
      setMessage("Bitte einen Titel angeben.");
      return;
    }
    const input: FormInput = {
      title: builderTitle,
      description: builderDescription,
      context_type: builderContext,
      context_ref: builderContextRef,
      assigned_role: builderAssignedRole,
      status: "aktiv",
      fields: builderFields.filter((field) => field.label.trim()).map(fromDraft),
    };
    if (editingId) {
      updateForm.mutate({ id: editingId, input }, {
        onSuccess: () => { setMessage("Formular gespeichert."); setView("list"); },
      });
    } else {
      createForm.mutate(input, {
        onSuccess: () => { setMessage("Formular angelegt."); setView("list"); },
      });
    }
  }

  function generateTemplate(key: string) {
    if (!key) return;
    fromTemplate.mutate({ template: key }, {
      onSuccess: (result) => { setMessage(`Vorlage angelegt: ${result.form.title}`); setView("list"); },
      onError: () => setMessage("Vorlage konnte nicht angelegt werden."),
    });
  }

  function openFill(id: string) {
    setSelectedId(id);
    setMessage("");
    setView("fill");
  }

  function openResponses(id: string) {
    setSelectedId(id);
    setMessage("");
    setView("responses");
  }

  function openAssignment(form: FormSummary) {
    setAssigning(form);
    setAssignRole("kunde");
    setAssignLabel("");
    setAssignDue("");
  }

  function submitAssignment(event: FormEvent) {
    event.preventDefault();
    if (!assigning) return;
    createAssignment.mutate({
      id: assigning.id, assignee_role: assignRole, assignee_label: assignLabel, due_date: assignDue,
    }, {
      onSuccess: () => {
        setMessage("Formular zugewiesen; der proaktive Hinweis ist aktiv.");
        setAssignLabel("");
        setAssignDue("");
      },
      onError: () => setMessage("Zuweisung konnte nicht gespeichert werden."),
    });
  }

  function submitFill(event: FormEvent) {
    event.preventDefault();
    if (!selectedId) return;
    const form = detail.data?.form;
    const missing = (form?.fields || []).filter((field) => field.required && (
      answers[field.id] === undefined || answers[field.id] === "" || answers[field.id] === false
    ));
    if (missing.length) {
      setMessage(`Bitte Pflichtfelder ausfüllen: ${missing.map((field) => field.label).join(", ")}`);
      return;
    }
    submit.mutate({
      id: selectedId,
      answers,
      submitted_role: role,
      assignment_id: fillAssignments.data?.items[0]?.id,
    }, {
      onSuccess: () => {
        setMessage("Antwort gespeichert. Vielen Dank.");
        setAnswers({});
        setView("responses");
      },
      onError: (error: unknown) => {
        const apiError = error as { details?: { detail?: { errors?: string[] } } };
        const errors = apiError?.details?.detail?.errors;
        setMessage(errors?.length ? `Bitte prüfen: ${errors.join(" · ")}` : "Antwort konnte nicht gespeichert werden.");
      },
    });
  }

  const items = list.data?.items ?? [];
  const stats = list.data?.stats;
  const groundedTemplates = (templates.data?.templates ?? []).filter((template) => template.grounded);

  return (
    <section className="fm-page">
      <header className="fm-head">
        <div>
          <h1>Formulare</h1>
          <p className="fm-sub">Erhebungen, Checklisten und Arbeitsformulare mit Zuweisung und Auswertung.</p>
        </div>
        {view === "list" && isBerater && (
          <div className="fm-head-actions">
            <button className="btn" onClick={newBuilder}>+ Neues Formular</button>
            <select className="fm-in" defaultValue="" onChange={(event) => {
              generateTemplate(event.target.value);
              event.currentTarget.value = "";
            }} title="Aus Vorlage erzeugen">
              <option value="">Aus Vorlage…</option>
              {(templates.data?.templates ?? []).map((template) => (
                <option key={template.key} value={template.key}>
                  {template.name}{template.source_code ? ` · ${template.source_code}` : ""}
                </option>
              ))}
            </select>
          </div>
        )}
        {view !== "list" && <button className="btn ghost" onClick={() => { setView("list"); setMessage(""); }}>← Übersicht</button>}
      </header>

      {message && <div className="fm-notice" role="status">{message}<button className="fm-notice-x" onClick={() => setMessage("")}>×</button></div>}

      {view === "list" && isBerater && groundedTemplates.length > 0 && (
        <section className="fm-template-band" aria-label="Methoden-Vorlagen">
          <strong>Methoden-Vorlagen</strong>
          <div className="fm-template-list">
            {groundedTemplates.map((template) => (
              <button key={template.key} onClick={() => generateTemplate(template.key)} disabled={fromTemplate.isPending}>
                <span>{template.name}</span><small>{template.source_code}</small>
              </button>
            ))}
          </div>
        </section>
      )}

      {view === "list" && (
        <>
          {stats && (
            <div className="fm-stats">
              <span>{stats.total} Formulare</span><span>{stats.aktiv} aktiv</span><span>{stats.responses} Antworten</span>
              {stats.pending_assigned > 0 && <span className="fm-pending">{stats.pending_assigned} Zuweisungen offen</span>}
              {list.data && !list.data.persistent && <span className="fm-warn">nicht persistent</span>}
            </div>
          )}
          {list.isLoading && <div className="fm-empty">Formulare werden geladen…</div>}
          {!list.isLoading && items.length === 0 && <div className="fm-empty">Noch keine Formulare vorhanden.</div>}
          <div className="fm-list">
            {items.map((form) => (
              <article key={form.id} className="fm-card">
                <div className="fm-card-main">
                  <div className="fm-card-title">{form.title}</div>
                  {form.description && <div className="fm-card-desc">{form.description}</div>}
                  <div className="fm-card-meta">
                    <span className={`fm-badge fm-st-${form.status}`}>{FORM_STATUS_LABEL[form.status] ?? form.status}</span>
                    <span>{CONTEXT_TYPE_LABEL[form.context_type] ?? form.context_type}{form.context_ref ? `: ${form.context_ref}` : ""}</span>
                    <span>{form.field_count} Felder</span><span>{form.response_count} Antworten</span>
                    {form.pending_assignment_count > 0 && <span className="fm-assigned">{form.pending_assignment_count} Zuweisung(en) offen</span>}
                    {!form.pending_assignment_count && form.assigned_role && <span className="fm-assigned">für {form.assigned_role}</span>}
                  </div>
                </div>
                <div className="fm-card-actions">
                  <button className="btn small" onClick={() => openFill(form.id)}>Ausfüllen</button>
                  <button className="btn ghost small" onClick={() => openResponses(form.id)}>Auswertung ({form.response_count})</button>
                  {isBerater && <button className="btn ghost small" onClick={() => openAssignment(form)}>Zuweisen</button>}
                  {isBerater && <button className="btn ghost small" onClick={() => editBuilder(form.id)}>Bearbeiten</button>}
                  {isBerater && <button className="btn ghost small" onClick={() => setStatus.mutate({
                    id: form.id, status: form.status === "aktiv" ? "archiviert" : "aktiv",
                  })}>{form.status === "aktiv" ? "Archivieren" : "Aktivieren"}</button>}
                  {isBerater && <button className="fm-del small" onClick={() => {
                    if (confirm(`Formular "${form.title}" inkl. Antworten löschen?`)) deleteForm.mutate(form.id);
                  }}>Löschen</button>}
                </div>
              </article>
            ))}
          </div>
        </>
      )}

      {view === "builder" && (
        <form className="fm-builder" onSubmit={saveBuilder}>
          <h2>{editingId ? "Formular bearbeiten" : "Neues Formular"}</h2>
          <label className="fm-field"><span>Titel *</span>
            <input className="fm-in" value={builderTitle} onChange={(event) => setBuilderTitle(event.target.value)} /></label>
          <label className="fm-field"><span>Beschreibung</span>
            <textarea className="fm-in" value={builderDescription} onChange={(event) => setBuilderDescription(event.target.value)} rows={2} /></label>
          <div className="fm-row fm-row-two">
            <label className="fm-field"><span>Kontext-Typ</span>
              <select className="fm-in" value={builderContext} onChange={(event) => setBuilderContext(event.target.value)}>
                {CONTEXT_TYPES.map((context) => <option key={context} value={context}>{CONTEXT_TYPE_LABEL[context] ?? context}</option>)}
              </select></label>
            <label className="fm-field"><span>Kontext-Bezug</span>
              <input className="fm-in" value={builderContextRef} onChange={(event) => setBuilderContextRef(event.target.value)} /></label>
          </div>
          <h3>Felder</h3>
          <div className="fm-fields">
            {builderFields.map((field, index) => (
              <div className="fm-field-row" key={index}>
                <input className="fm-in" placeholder="Feldbezeichnung" value={field.label}
                  onChange={(event) => setBuilderFields(builderFields.map((item, itemIndex) => itemIndex === index ? { ...item, label: event.target.value } : item))} />
                <select className="fm-in" value={field.type}
                  onChange={(event) => setBuilderFields(builderFields.map((item, itemIndex) => itemIndex === index ? { ...item, type: event.target.value as FieldType } : item))}>
                  {FIELD_TYPES.map((type) => <option key={type} value={type}>{FIELD_TYPE_LABEL[type]}</option>)}
                </select>
                {field.type === "select" && <input className="fm-in" placeholder="Optionen (Komma-getrennt)" value={field.optionsText}
                  onChange={(event) => setBuilderFields(builderFields.map((item, itemIndex) => itemIndex === index ? { ...item, optionsText: event.target.value } : item))} />}
                <label className="fm-req"><input type="checkbox" checked={field.required}
                  onChange={(event) => setBuilderFields(builderFields.map((item, itemIndex) => itemIndex === index ? { ...item, required: event.target.checked } : item))} /> Pflicht</label>
                <button type="button" className="fm-del small" onClick={() => setBuilderFields(builderFields.filter((_, itemIndex) => itemIndex !== index))}>×</button>
              </div>
            ))}
          </div>
          <button type="button" className="btn ghost small" onClick={() => setBuilderFields([
            ...builderFields, { label: "", type: "text", required: false, optionsText: "", help: "" },
          ])}>+ Feld hinzufügen</button>
          <div className="fm-builder-foot">
            <button className="btn" type="submit" disabled={createForm.isPending || updateForm.isPending}>{editingId ? "Speichern" : "Formular anlegen"}</button>
            <button className="btn ghost" type="button" onClick={() => { setView("list"); setMessage(""); }}>Abbrechen</button>
          </div>
        </form>
      )}

      {view === "fill" && detail.data?.form && (
        <form className="fm-fill" onSubmit={submitFill}>
          <h2>{detail.data.form.title}</h2>
          {detail.data.form.description && <p className="fm-fill-desc">{detail.data.form.description}</p>}
          {fillAssignments.data?.items[0] && (
            <div className="fm-assignment-note">
              Zugewiesen an {fillAssignments.data.items[0].assignee_label || fillAssignments.data.items[0].assignee_role}
              {fillAssignments.data.items[0].due_date ? ` · fällig ${fillAssignments.data.items[0].due_date}` : ""}
            </div>
          )}
          {detail.data.form.fields.length === 0 && <p className="fm-empty">Dieses Formular hat noch keine Felder.</p>}
          {detail.data.form.fields.map((field) => (
            <label className="fm-field" key={field.id}>
              <span>{field.label}{field.required && <em className="fm-star"> *</em>}</span>
              {field.type === "textarea" ? (
                <textarea className="fm-in" rows={3} value={String(answers[field.id] ?? "")}
                  onChange={(event) => setAnswers({ ...answers, [field.id]: event.target.value })} />
              ) : field.type === "select" ? (
                <select className="fm-in" value={String(answers[field.id] ?? "")}
                  onChange={(event) => setAnswers({ ...answers, [field.id]: event.target.value })}>
                  <option value="">— bitte wählen —</option>
                  {field.options.map((option) => <option key={option} value={option}>{option}</option>)}
                </select>
              ) : field.type === "checkbox" ? (
                <input type="checkbox" checked={Boolean(answers[field.id])}
                  onChange={(event) => setAnswers({ ...answers, [field.id]: event.target.checked })} />
              ) : (
                <input className="fm-in" type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"}
                  value={String(answers[field.id] ?? "")}
                  onChange={(event) => setAnswers({ ...answers, [field.id]: event.target.value })} />
              )}
              {field.help && <small className="fm-help">{field.help}</small>}
            </label>
          ))}
          <div className="fm-builder-foot">
            <button className="btn" type="submit" disabled={submit.isPending || detail.data.form.fields.length === 0}>Antwort speichern</button>
            <button className="btn ghost" type="button" onClick={() => openResponses(detail.data!.form.id)}>Auswertung ansehen</button>
          </div>
        </form>
      )}

      {view === "responses" && (
        <div className="fm-responses">
          <div className="fm-resp-head">
            <h2>Auswertung</h2>
            <div className="fm-resp-actions">
              {selectedId && <button className="btn small" onClick={() => openFill(selectedId)}>+ Ausfüllen</button>}
              {selectedId && (responses.data?.count ?? 0) > 0 && <button className="btn ghost small" onClick={() =>
                exportCsv(selectedId, `formular_${selectedId}`).catch(() => setMessage("Export fehlgeschlagen."))
              }>CSV exportieren ↓</button>}
            </div>
          </div>
          {aggregation.data && aggregation.data.response_count > 0 && (
            <section className="fm-aggregation" aria-label="Aggregierte Antworten">
              <div className="fm-aggregation-head"><strong>{aggregation.data.response_count} Antworten</strong><span>{aggregation.data.privacy}</span></div>
              <div className="fm-aggregation-grid">{aggregation.data.fields.map((field) => <AggregateField key={field.id} field={field} />)}</div>
            </section>
          )}
          {responses.isLoading && <div className="fm-empty">Antworten werden geladen…</div>}
          {responses.data && responses.data.count === 0 && <div className="fm-empty">Noch keine Antworten.</div>}
          {responses.data && responses.data.count > 0 && (
            <div className="fm-table-wrap">
              <table className="fm-table">
                <thead><tr><th>Eingereicht</th><th>Von</th>
                  {responses.data.fields.map((field) => <th key={field.id}>{field.label}</th>)}{isBerater && <th></th>}
                </tr></thead>
                <tbody>{responses.data.items.map((response) => (
                  <tr key={response.id}>
                    <td>{response.created_at?.slice(0, 16).replace("T", " ")}</td>
                    <td>{response.submitted_by || "—"}{response.submitted_role ? ` (${response.submitted_role})` : ""}</td>
                    {responses.data!.fields.map((field) => {
                      const value = response.answers[field.id];
                      return <td key={field.id}>{typeof value === "boolean" ? (value ? "ja" : "nein") :
                        (value === undefined || value === null || value === "" ? "—" : String(value))}</td>;
                    })}
                    {isBerater && <td><button className="fm-del small" onClick={() => {
                      if (selectedId && confirm("Antwort löschen?")) deleteResponse.mutate({ id: selectedId, rid: response.id });
                    }}>×</button></td>}
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {assigning && (
        <div className="fm-modal-backdrop" role="presentation">
          <section className="fm-modal" role="dialog" aria-modal="true" aria-labelledby="fm-assign-title">
            <div className="fm-modal-head"><h2 id="fm-assign-title">Formular zuweisen</h2>
              <button className="fm-notice-x" onClick={() => setAssigning(null)}>×</button></div>
            <strong>{assigning.title}</strong>
            <form className="fm-assign-form" onSubmit={submitAssignment}>
              <label className="fm-field"><span>Rolle</span><select className="fm-in" value={assignRole}
                onChange={(event) => setAssignRole(event.target.value as "kunde" | "berater" | "alle")}>
                <option value="kunde">Kunde</option><option value="berater">Berater</option><option value="alle">Alle</option>
              </select></label>
              <label className="fm-field"><span>Empfänger/Team (optional)</span><input className="fm-in" value={assignLabel}
                onChange={(event) => setAssignLabel(event.target.value)} /></label>
              <label className="fm-field"><span>Fällig am</span><input className="fm-in" type="date" value={assignDue}
                onChange={(event) => setAssignDue(event.target.value)} /></label>
              <button className="btn" type="submit" disabled={createAssignment.isPending}>Zuweisen</button>
            </form>
            <div className="fm-assignment-list">
              {(assignments.data?.items ?? []).map((assignment) => (
                <div key={assignment.id}>
                  <span>{assignment.assignee_label || assignment.assignee_role || assignment.assignee_sub}
                    {assignment.due_date ? ` · ${assignment.due_date}` : ""}</span>
                  <span className={`fm-badge fm-assignment-${assignment.status}`}>{assignment.status}</span>
                  {assignment.status === "offen" && <button className="fm-del small" onClick={() =>
                    cancelAssignment.mutate({ id: assigning.id, assignmentId: assignment.id })
                  }>Stornieren</button>}
                </div>
              ))}
            </div>
            <div className="fm-builder-foot"><button className="btn ghost" onClick={() => setAssigning(null)}>Schließen</button></div>
          </section>
        </div>
      )}

      <footer className="fm-foot">Mandantengetrennte Formulare, Antworten und Zuweisungen mit explizitem Tenant-Filter.</footer>
    </section>
  );
}
